import React, { useState } from 'react'
import { Trash2, Loader2, AlertTriangle } from 'lucide-react'
import { Modal } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useContactStore from '../../stores/contactStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Contact } from '../../types/contact'

interface DeleteContactModalProps {
  isOpen: boolean
  onClose: () => void
  contact: Contact | null
  onContactDeleted?: () => void
}

const DeleteContactModal: React.FC<DeleteContactModalProps> = ({
  isOpen,
  onClose,
  contact,
  onContactDeleted
}) => {
  const { showSuccess, showError } = useToast()
  const { deleteContact } = useContactStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    if (!contact) return

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    setIsDeleting(true)

    try {
      await deleteContact(contact.id, String(selectedCustomer.id))
      showSuccess('Contact Deleted', `"${contact.first_name} ${contact.last_name}" has been deleted successfully`)
      onContactDeleted?.()
      onClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to delete contact',
        errorMessage.includes('not found')
          ? 'Contact not found'
          : 'Please try again later'
      )
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Contact"
      size="md"
      showCloseButton={true}
    >
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
        </div>

        {/* Message */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the contact{' '}
            <span className="font-semibold text-gray-900">
              {contact?.first_name} {contact?.last_name}
            </span>
            ?
          </p>
          {contact?.is_primary && (
            <p className="mt-2 text-sm text-amber-600">
              This is a primary contact for{' '}
              <span className="font-semibold">{contact.partners.name}</span>.
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            This action cannot be undone.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Contact
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteContactModal
